import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle, faCrown, faUserTag } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { BankInfo } from '../../types';

interface ProfileData {
    displayName: string;
    email?: string;
    createdAt?: number;
    lastAccess?: number;
    bankInfo?: BankInfo;
    isAnonymous: boolean;
    isOwner: boolean;
    userId: string;
}

interface ProfileHeaderProps {
    displayData: ProfileData;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ displayData }) => {
    const { t } = useTranslation();

    // Get role badge info similar to UserWelcome
    const getRoleBadge = () => {
        if (displayData.isOwner) {
            return {
                label: t('profile.components.profileHeader.roles.eventOwner'),
                icon: faCrown,
                color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
            };
        }

        if (displayData.isAnonymous) {
            return {
                label: t('profile.components.profileHeader.roles.guestUser'),
                icon: faUserTag,
                color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
            };
        }

        return {
            label: t('profile.components.profileHeader.roles.participant'),
            icon: faUserCircle,
            color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
        };
    };

    const roleBadge = getRoleBadge();

    return (
        <div className="flex items-center space-x-4">
            {/* Avatar - matching UserWelcome style */}
            <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full">
                <FontAwesomeIcon icon={faUserCircle} className="text-xl" />
            </div>

            {/* User Info */}
            <div>
                <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {displayData.displayName}
                    </h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${roleBadge.color}`}>
                        <FontAwesomeIcon icon={roleBadge.icon} className="mr-1" />
                        {roleBadge.label}
                    </span>
                </div>

                {/* Additional context - similar to UserWelcome's conditional text */}
                {displayData.isAnonymous && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('profile.components.profileHeader.descriptions.guestParticipant')}
                    </p>
                )}

                {displayData.isOwner && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('profile.components.profileHeader.descriptions.eventOwnerAdmin')}
                    </p>
                )}
            </div>
        </div>
    );
};

export default ProfileHeader;